import { Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  collectionData,
  collectionSnapshots,
  deleteField,
  doc,
  documentId,
  Firestore,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QuerySnapshot,
  setDoc,
  startAfter,
  updateDoc,
  where
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { getEvent } from '@yoimo/client-sdk/events';
import { getNamedTagById as getNamedTagByIdFromModule, NamedTagWithId } from '@yoimo/client-sdk/named-tags';
import { getChannelPageById, PageWithDocId } from '@yoimo/client-sdk/pages';
import { getUsersSubscriptions } from '@yoimo/client-sdk/subscriptions';
import {
  CustomAttributes, Event, NamedTag, Page,
  SoldTicket,
  Subscription
} from '@yoimo/interfaces';
import { chunk } from 'lodash';
import { auditTime, firstValueFrom, from, map, Observable, switchMap, take, tap } from 'rxjs';

export type MediaConversionJob = CustomAttributes & {
  id: string;
  name: string;
  description: string;
  clip: null | string;
  channelDocId: string;
  duration: number;
  inputUrl: string;
  posterImage: string;
  output?: 'HLS' | 'MP4';
  status?: 'SUBMITTED';
  tags: string[];
  type: string;
  videoLocation: null | string;
  eventId: string | null;
  livestreamDocId: null | string;
  // restrictedTo: string[];
  restrictedTo: string[] | { exclude: string[] };
  unlocked: boolean;
  createdTime: Date;
  country: string;
  published: boolean;
  opaque?: any;
};

@Injectable({
  providedIn: 'root',
})
export class MasterChannelService {
  endOfChannel = false;
  endOfEvent = false;
  eventSearchText = '';
  tagsSearchText = '';
  subscribersLimit = 20;
  planSearchText = '';
  pagesSearchText = '';
  attachedChannelSearchText = '';
  rowId = '';

  constructor(private readonly firestore: Firestore, private readonly functions: Functions) { }

  async getVideoSource(docId: string) {
    const docRef = collection(this.firestore, 'channels', docId, 'videos');
    return getDocs(docRef);
  }

  async getSource(docId: string, subCollection: string) {
    const docRef = collection(this.firestore, 'channels', docId, subCollection);
    return getDocs(docRef);
  }

  async getVideoSourceByEventId(docId: string, eventId: string){
    const docRef = collection(this.firestore, 'channels', docId, 'videos');
    const q = query(docRef, where('eventId', '==', eventId), where('archived', '==', false), where('published', '==', true));
    return getDocs(q);
  }

  async getEventSource(docId: string) {
    const docRef = collection(this.firestore, 'channels', docId, 'events');
    return getDocs(docRef);
  }

  async getActiveEventList(
    channelDocId: string
  ): Promise<{ name: string; docId: string }[]> {
    const docRef = collection(this.firestore, 'channels', channelDocId, 'events');
    const q = query(docRef, where('archived', '==', false));
    const eventDocs= await getDocs(q);

    return eventDocs.docs
      .map((event) => ({
        name: event.get("title"),
        docId: event.id,
      }));
  }

  async getTagSource(docId: string) {
    const docRef = collection(this.firestore, 'channels', docId, 'tags');
    return getDocs(docRef);
  }

  getEvents(channelDocId: string) {
    const eventsRef = collection(
      this.firestore,
      'channels',
      channelDocId,
      'events'
    );
    const q = query(eventsRef, orderBy('startTime', 'desc'));
    return collectionData(q, { idField: 'docId' }) as Observable<Event[]>;
  }

  async getVideosByIdArray(values: string[], channelDocId: string) {
    return (
      await Promise.all(
        chunk(values, 10).map(
          slice => {
            const videosRef = collection(this.firestore, 'channels', channelDocId, 'videos');
            const q = query(videosRef, where(documentId(), 'in', slice));
            return getDocs(q);
          }
        )
      )
    ).reduce((acc: any, batchResult) => [...acc, ...batchResult.docs], []);
  }

  getPages(channelDocId: string) {
    const pagesRef = collection(
      this.firestore,
      'channels',
      channelDocId,
      'pages'
    );
    return collectionData(pagesRef, { idField: 'id' });
  }

  getPageById(channelDocId: string, pageDocId: string): Observable<PageWithDocId | undefined> {
    return getChannelPageById(this.firestore, channelDocId, pageDocId);
  }

  createEvent(channelDocId: string, eventDocId: string, obj: Event) {
    const eventsRef = collection(
      this.firestore,
      'channels',
      channelDocId,
      'events'
    );
    return setDoc(doc(eventsRef, eventDocId), obj);
  }

  createSoldTicket(channelDocId: string, soldTicketData: SoldTicket) {
    const soldTicketsRef = collection(
      this.firestore,
      'channels',
      channelDocId,
      'soldTickets'
    );
    return addDoc(soldTicketsRef, soldTicketData);
  }

  async getExistingSoldTicket(
    channelId: string, 
    userId: string, 
    eventTicketId: string
  ): Promise<(SoldTicket & { id: string }) | null> {
    const soldTicketsRef = collection(
      this.firestore, 
      `channels/${channelId}/soldTickets`
    );
    
    const q = query(
      soldTicketsRef,
      where('userId', '==', userId),
      where('eventTicketId', '==', eventTicketId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...(doc.data() as SoldTicket),
    } as SoldTicket & { id: string };
  }

  async checkExistingSoldTicket(
    channelId: string, 
    userId: string, 
    eventTicketId: string
  ): Promise<boolean> {
    const existingTicket = await this.getExistingSoldTicket(channelId, userId, eventTicketId);
    return !!existingTicket;
  }

  createPage(channelDocId: string, pageDocId: string, obj: any) {
    const pagesRef = collection(
      this.firestore,
      'channels',
      channelDocId,
      'pages'
    );
    return setDoc(doc(pagesRef, pageDocId), obj, { merge: true });
  }

  editEvent(channelDocId: string, eventDocId: string, obj: Partial<Event>) {
    const eventsRef = collection(
      this.firestore,
      'channels',
      channelDocId,
      'events'
    );
    return updateDoc(doc(eventsRef, eventDocId), obj);
  }

  async updateEvents(
    channelDocId: string,
    eventDocId: string,
    obj: Partial<Event | Page>,
    type: 'DETAILS' | 'TICKETS' | 'PAGES',
    pagePropsToDelete: Partial<keyof Page>[] = []
  ): Promise<void> {
    const channelCollectionRef = collection(
      this.firestore,
      'channels',
      channelDocId,
      'events'
    );

    return new Promise<void>((resolve, reject) => {
      const docRef = doc(channelCollectionRef, eventDocId);
      getDoc(docRef).then((docSnapshot) => {
        if (docSnapshot.exists()) {
          const eventData = docSnapshot.data();
          
          if (type === 'PAGES') {
            const pages = eventData.pages;

            if (pages && pages.length > 0) {
              pages[0] = { ...pages[0], ...obj };
              for (const property of pagePropsToDelete) {
                delete pages[0][property];
              }
              
              updateDoc(doc(channelCollectionRef, eventDocId), {
                pages: pages,
              })
                .then(() => {
                  resolve();
                })
                .catch((error: any) => {
                  console.error('Error updating document: ', error);
                  reject(error);
                });
            } else {
              reject(new Error('No pages found in the document.'));
            }
          } else {
            // DETAILS or TICKETS case (default)
            updateDoc(doc(channelCollectionRef, eventDocId), {
              ...obj,
            })
              .then(() => {
                resolve();
              })
              .catch((error: any) => {
                console.error('Error updating document: ', error);
                reject(error);
              });
          }
        } else {
          reject(new Error('Document does not exist.'));
        }
      });
    });
  }

  getSubscribersPaginated(channelDocId: string, lastSubscriptionId?: string) {
    const subscribersRef = collection(this.firestore, 'channels', channelDocId, 'subscriptions');
    let subscribersQuery;
    if (lastSubscriptionId) {
      const lastDocRef = doc(this.firestore, 'channels', channelDocId, 'subscriptions', lastSubscriptionId);
      return from(getDoc(lastDocRef)).pipe(
        switchMap(lastDocSnap => {
          subscribersQuery = query(
            subscribersRef,
            where('subscriptionId', '==', 'INTERNAL:0:1'),
            orderBy('creationDate', 'desc'),
            startAfter(lastDocSnap),
            limit(this.subscribersLimit)
          );
          return collectionData(subscribersQuery, { idField: 'docId' });
        })
      );
    } else {
      subscribersQuery = query(
        subscribersRef,
        where('subscriptionId', '==', 'INTERNAL:0:1'),
        orderBy('creationDate', 'desc'),
        limit(this.subscribersLimit)
      );
      return collectionData(subscribersQuery, { idField: 'docId' });
    }
  }
  
  getSubscriptions(channelDocId: string) {
    const subscriptionsRef = collection(this.firestore, 'channels', channelDocId, 'plans');
    return collectionData(subscriptionsRef, { idField: 'docId' });
  }

  updatePublishStateEvent(channelId: string, eventId: string, status: boolean) {
    const eventDocRef = doc(
      this.firestore,
      'channels',
      channelId,
      'events',
      eventId
    );
    return updateDoc(eventDocRef, { published: status });
  }

  updatePagesState(
    channelId: string,
    pageId: string,
    obj:
      | {
        status?: string;
      }
      | {
        active?: boolean;
      }
  ) {
    const pageDocRef = doc(
      this.firestore,
      'channels',
      channelId,
      'pages',
      pageId
    );
    return updateDoc(pageDocRef, obj);
  }

  updateSubscriptionState(
    channelId: string,
    subId: string,
    obj: {
      available?: boolean;
    }
  ) {

    const subDocRef = doc(this.firestore, 'channels', channelId, 'plans', subId);
    return updateDoc(subDocRef, obj);
  }

  updateUnarchiveStateEvent(
    channelId: string,
    eventId: string,
    status: boolean
  ) {
    const eventDocRef = doc(
      this.firestore,
      'channels',
      channelId,
      'events',
      eventId
    );
    return updateDoc(eventDocRef, { archived: status });
  }

  async updateArchiveStateEvent(channelId: string, eventId: string) {
    const archiveEvent = httpsCallable(this.functions, 'events-archiveevent');
    return (await archiveEvent({ eventId: eventId, channelId: channelId }))
      .data;
  }

  createNamedTag(channelDocId: string, tagDocId: string, obj: NamedTag) {
    const tagDocRef = doc(
      this.firestore,
      'channels',
      channelDocId,
      'tags',
      tagDocId
    );
    return setDoc(tagDocRef, obj);
  }

  setNamedTagPage(channelDocId: string, tagDocId: string, page: Page[]) {
    const tagDocRef = doc(
      this.firestore,
      'channels',
      channelDocId,
      'tags',
      tagDocId
    );
    return setDoc(tagDocRef, { pages: page }, { merge: true });
  }

  updateNamedTag(
  channelDocId: string,
  tagDocId: string,
  obj: Partial<NamedTag | Page>,
  type: 'DETAILS' | 'PAGES',
  pagePropsToDelete: Partial<keyof Page>[] = []
): Promise<void> {
  const channelCollectionRef = collection(
    this.firestore,
    'channels',
    channelDocId,
    'tags'
  );

  return new Promise<void>((resolve, reject) => {
    getDoc(doc(channelCollectionRef, tagDocId)).then((data) => {
      if (!data.exists()) {
        reject(new Error('Document does not exist.'));
        return;
      }

      const tagData = data.data();

      if (type === 'PAGES') {
        const pages = tagData.pages;

        if (pages && pages.length > 0) {
          pages[0] = { ...pages[0], ...obj };

          for (const property of pagePropsToDelete) {
            delete pages[0][property];
          }

          updateDoc(doc(channelCollectionRef, tagDocId), { pages })
            .then(() => resolve())
            .catch((error) => {
              console.error('Error updating document: ', error);
              reject(error);
            });
        } else {
          reject(new Error('No pages found in the document.'));
        }
      } else {
        // DETAILS
        updateDoc(doc(channelCollectionRef, tagDocId), { ...obj })
          .then(() => resolve())
          .catch((error) => {
            console.error('Error updating document: ', error);
            reject(error);
          });
      }
    });
  });
}


  updatePages(
    channelDocId: string,
    pageId: string,
    obj: any,
    pagePropsToDelete: Partial<keyof Page>[] = []
  ): Promise<void> {
    const channelCollectionRef = collection(
      this.firestore,
      'channels',
      channelDocId,
      'pages'
    );

    return new Promise<void>((resolve, reject) => {
      const docRef = doc(channelCollectionRef, pageId);
      getDoc(docRef).then((docSnapshot) => {
        if (docSnapshot.exists()) {
          let pages = docSnapshot.data();
          if (Object.keys(pages).length) {
            pages = { ...pages, ...obj };
            for (const property of pagePropsToDelete)
              {pages[property] = deleteField();}

            updateDoc(doc(channelCollectionRef, pageId), {
              ...pages,
            })
              .then(() => {
                resolve();
              })
              .catch((error: any) => {
                console.error('Error updating document: ', error);
                reject(error);
              });
          } else {
            reject(new Error('No pages found in the document.'));
          }
        } else {
          reject(new Error('Document does not exist.'));
        }
      });
    });
  }

  getNamedTags(channelDocId: string) {
    const tagsRef = collection(
      this.firestore,
      'channels',
      channelDocId,
      'tags'
    );
    return collectionData(tagsRef, { idField: 'docId' });
  }

  async isUniqueNamedTag(channelDocId: string, tagName: string) {
    const tagsRef = collection(
      this.firestore,
      'channels',
      channelDocId,
      'tags'
    );
    const q = query(tagsRef, where('name', '==', tagName), limit(1));
    const querySnapshot = await getDocs(q);
    return querySnapshot.empty;
  }

  getNamedTagById(channelDocId: string, tagDocId: string): Observable<NamedTagWithId | undefined> {
    return getNamedTagByIdFromModule(this.firestore, channelDocId, tagDocId);
  }

  updateNamedTagById(channelDocId: string, tagDocId: string, obj: Partial<NamedTag>) {
    const tagDocRef = doc(
      this.firestore,
      'channels',
      channelDocId,
      'tags',
      tagDocId
    );
    return updateDoc(tagDocRef, obj);
  }
  
  updatePlanById(channelDocId: string, planDocId: string, obj: any) {
    const planDocRef = doc(this.firestore, 'channels', channelDocId, 'plans', planDocId);
    return updateDoc(planDocRef, obj);
  }

  createConversionJob(video: Record<string, any>, additionalFields: Record<string, any>) {
    return {
      channelDocId: additionalFields.audience.channelId,
      clip: null,
      createdTime: new Date(),
      inputUrl: video.inputUrl,
      name: video.name.trim(),
      opaque: additionalFields,
      output: video.type === 'UPLOAD' ? 'HLS' : 'MP4' as MediaConversionJob['output'],
      status: 'SUBMITTED' as MediaConversionJob['status'],
      type: video.type,
      videoLocation: null,
    } as MediaConversionJob;
  }

  createUpload(docId: string, obj: Partial<MediaConversionJob>) {
    const conversionDocRef = doc(this.firestore, 'mediaConversionJobs', docId);
    return setDoc(conversionDocRef, obj);
  }

  updateMediaConversionJob(docId: string, obj: Record<string, unknown>) {
    const conversionDocRef = doc(this.firestore, 'mediaConversionJobs', docId);
    return setDoc(conversionDocRef, obj, { merge: true });
  }

  getBatch(lastSeen: string) {
    const channelsRef = collection(this.firestore, 'channels');
    const q = query(
      channelsRef,
      orderBy('name'),
      startAfter(lastSeen),
      limit(50)
    );

    return collectionSnapshots(q).pipe(
      auditTime(400),
      take(1),
      tap((arr) => {
        if (!arr.length) {
          this.endOfChannel = true;
        }
      }),
      map((arr) =>
        arr?.reduce((acc, cur) => {
          const id = cur.id;
          const data = cur.data() as Record<string, unknown>;
          return { ...acc, [id]: { id, ...data } };
        }, {})
      )
    );
  }

  getChannels() {
    const channelsRef = collection(this.firestore, 'channels');
    const q = query(channelsRef, orderBy('name'));
    return collectionData(q, { idField: 'id' });
  }

  getEventById(channelDocId: string, eventDocId: string): Observable<Event | undefined> {
    return getEvent(this.firestore, channelDocId, eventDocId);
  }

  async getChannelsByIdArray(values: string[]) {
    return (
      await Promise.all(
        chunk(values, 10).map(
          slice => {
            const channelsRef = collection(this.firestore, 'channels');
            const q = query(channelsRef, where(documentId(), 'in', slice));
            return getDocs(q);
          }
        )
      )
    ).reduce((acc: any, batchResult) => [...acc, ...batchResult.docs], []);
  }

  async getVideosOfChannelByIdAndType(channelDocId: string, videoType: string[]) {
    const videosRef = collection(this.firestore, 'channels', channelDocId, 'videos');
    const q = query(videosRef, where('type', 'in', videoType), where('archived', '==', false));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      name: doc.data().name,
      docId: doc.id
    }));
  }

  async getEventsByIdArray(values: string[], channelDocId: string) {
    return (
      await Promise.all(
        chunk(values, 10).map(
          slice => {
            const eventsRef = collection(this.firestore, 'channels', channelDocId, 'events');
            const q = query(eventsRef, where(documentId(), 'in', slice));
            return getDocs(q);
          }
        )
      )
    ).reduce((acc: any, batchResult) => [...acc, ...batchResult.docs], []);
  }

  async getMediaConversionJobs(liveStreamDocIds: string[]) {
    const results = await Promise.all(
      chunk(liveStreamDocIds, 10).map((slice) => {
        const jobsRef = collection(this.firestore, 'mediaConversionJobs');
        const q = query(jobsRef, where('livestreamDocId', 'in', slice));
        return getDocs(q);
      })
    );

    return results.reduce((acc: any, batchResult: QuerySnapshot) => [...acc, ...batchResult.docs], []);
  }

  getMediaConversionDoc(liveStreamId: string) {
    const jobsRef = collection(this.firestore, 'mediaConversionJobs');
    const q = query(jobsRef, where('livestreamDocId', '==', liveStreamId));
    return collectionData(q).pipe(auditTime(5000));
  }

  async getChannelById(channelId: string) {
    const channelDocRef = doc(this.firestore, 'channels', channelId);
    return getDoc(channelDocRef);
  }

  getMasterChannel(channelId: string) {
    const clubsRef = collection(this.firestore, 'clubs');
    const q = query(
      clubsRef,
      where('channelDetails.channelId', '==', channelId)
    );
    return collectionData(q, { idField: 'docId' });
  }

  async getPlanSource(docId: string) {
    const plansRef = collection(this.firestore, 'channels', docId, 'plans');
    return getDocs(plansRef);
  }

  async assignTicket(channelDocId: string, sourceVideoId: string, targetVideoId: string){
    const callable = httpsCallable(this.functions, 'events-updateticketvideoaccess');
    const response = await callable({secondPartVideoId:targetVideoId, channelId:channelDocId, videoId:sourceVideoId});
    return response.data;
  }

 async getAllSoldTicketsForUserAndTicket(channelId: string, userId: string, eventTicketId: string): Promise<SoldTicket[]> {
    const soldTicketsRef = collection(
      this.firestore,
      `channels/${channelId}/soldTickets`
    );
    const q = query(
      soldTicketsRef,
      where('userId', '==', userId),
      where('eventTicketId', '==', eventTicketId)
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return [];
    }
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data
      } as SoldTicket & {id: string};
    });
  }
  async addSubscription(channelId: string, subscriptionData: Subscription): Promise<void> {
    const subscriptionsCollectionRef = collection(this.firestore, 'channels', channelId, 'subscriptions');
    const newSubscriptionDocRef = doc(subscriptionsCollectionRef); 
    await setDoc(newSubscriptionDocRef, subscriptionData);
  }

  async getUserSubscriptions(channelId: string, userId: string): Promise<Subscription[]> {
    try {
      const subs = await firstValueFrom(
        getUsersSubscriptions(this.firestore, channelId, userId)
      );

      return subs.map(s => ({
        ...(s as Subscription),
      })) as Subscription[];
    } catch (error) {
      console.error('Error getting user subscriptions:', error);
      return [];
    }
  }
}
